import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import Modal from './Modal';

export default function LocationPicker({ value, onChange }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const [countryId, setCountryId] = useState('');
  const [divisionId, setDivisionId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [areaId, setAreaId] = useState('');

  // Temporary state for the modal
  const [tempIds, setTempIds] = useState({ country: '', division: '', district: '', area: '' });

  // To prevent infinite loops when updating path from value
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    api.get('/locations?tree=1').then(res => {
      setLocations(res.locations || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!value || locations.length === 0 || isInitialized) return;
    
    let path = [];
    const findPath = (nodes, targetId, currentPath = []) => {
      for (const node of nodes) {
        if (node.id === parseInt(targetId)) {
          path = [...currentPath, node];
          return true;
        }
        if (node.children && findPath(node.children, targetId, [...currentPath, node])) {
          return true;
        }
      }
      return false;
    };
    
    findPath(locations, value);
    
    if (path.length > 0) {
      const pCountry = path.find(p => p.type === 'COUNTRY');
      const pDivision = path.find(p => p.type === 'DIVISION');
      const pDistrict = path.find(p => p.type === 'DISTRICT');
      const pArea = path.find(p => p.type === 'AREA');
      
      if (pCountry) setCountryId(pCountry.id.toString());
      if (pDivision) setDivisionId(pDivision.id.toString());
      if (pDistrict) setDistrictId(pDistrict.id.toString());
      if (pArea) setAreaId(pArea.id.toString());
    }
    setIsInitialized(true);
  }, [value, locations, isInitialized]);

  if (loading) return <div style={{ fontSize: '12px', color: 'var(--text3)', padding: '10px' }}>Loading locations...</div>;

  const countries = locations;
  const selectedCountry = countries.find(c => c.id.toString() === countryId);
  const divisions = selectedCountry?.children || [];
  const selectedDivision = divisions.find(d => d.id.toString() === divisionId);
  const districts = selectedDivision?.children || [];
  const selectedDistrict = districts.find(d => d.id.toString() === districtId);
  const areas = selectedDistrict?.children || [];

  const handleCountry = e => {
    const val = e.target.value;
    setTempIds({ country: val, division: '', district: '', area: '' });
  };
  const handleDivision = e => {
    const val = e.target.value;
    setTempIds(prev => ({ ...prev, division: val, district: '', area: '' }));
  };
  const handleDistrict = e => {
    const val = e.target.value;
    setTempIds(prev => ({ ...prev, district: val, area: '' }));
  };
  const handleArea = e => {
    const val = e.target.value;
    setTempIds(prev => ({ ...prev, area: val }));
  };

  const handleConfirm = () => {
    const { country, division, district, area } = tempIds;
    setCountryId(country); setDivisionId(division); setDistrictId(district); setAreaId(area);
    
    const finalId = area || district || division || country;
    onChange(finalId ? parseInt(finalId) : null);
    setShowModal(false);
  };

  const getName = (nodes, id) => {
    if (!id) return null;
    for (const node of nodes) {
      if (node.id === parseInt(id)) return node.name;
      if (node.children) {
        const found = getName(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const currentLocationName = [
    getName(locations, countryId),
    getName(locations, divisionId),
    getName(locations, districtId),
    getName(locations, areaId)
  ].filter(Boolean).join(' > ') || 'Select Location';

  const openModal = () => {
    setTempIds({ country: countryId, division: divisionId, district: districtId, area: areaId });
    setShowModal(true);
  };

  const tCountry = locations.find(c => c.id.toString() === tempIds.country);
  const tDivisions = tCountry?.children || [];
  const tDivision = tDivisions.find(d => d.id.toString() === tempIds.division);
  const tDistricts = tDivision?.children || [];
  const tDistrict = tDistricts.find(d => d.id.toString() === tempIds.district);
  const tAreas = tDistrict?.children || [];

  return (
    <>
      <div 
        className="form-input" 
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface)' }}
        onClick={openModal}
      >
        <span style={{ color: countryId ? 'var(--text)' : 'var(--text3)' }}>
          📍 {currentLocationName}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text3)' }}>▼</span>
      </div>

      <Modal open={showModal} onClose={() => setShowModal(false)} title="Select Location">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px' }}>
          <div className="form-group">
            <label className="form-label">Country</label>
            <select className="form-input" value={tempIds.country} onChange={handleCountry}>
              <option value="">Select Country</option>
              {locations.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          
          {tDivisions.length > 0 && (
            <div className="form-group">
              <label className="form-label">Division/State</label>
              <select className="form-input" value={tempIds.division} onChange={handleDivision}>
                <option value="">Select Division/State</option>
                {tDivisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}
          
          {tDistricts.length > 0 && (
            <div className="form-group">
              <label className="form-label">District/City</label>
              <select className="form-input" value={tempIds.district} onChange={handleDistrict}>
                <option value="">Select District/City</option>
                {tDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}
          
          {tAreas.length > 0 && (
            <div className="form-group">
              <label className="form-label">Area/Location</label>
              <select className="form-input" value={tempIds.area} onChange={handleArea}>
                <option value="">Select Area/Location</option>
                {tAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          )}

          <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
            <button className="btn btn-outline btn-full" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-primary btn-full" onClick={handleConfirm}>Confirm</button>
          </div>
        </div>
      </Modal>
    </>
  );
}
